import React from 'react';
import styles from './index.module.scss';
import { useScrollTrigger } from '@/hooks/useScrollTrigger';
import detail1Src from '@/assets/aixuephonics2-detail1.png';
import detail2Src from '@/assets/aixuephonics2-detail2.png';
import detail3Src from '@/assets/aixuephonics2-detail3.png';
import detail4Src from '@/assets/aixuephonics2-detail4.png';

export default function Screen2() {
  const rootRef = useScrollTrigger({
    triggerClass: styles['screen2-anim-start'],
    triggerRatio: 0.5,
    scrollContainerSelector: '[data-scroll-root="aixue-phonics"]',
    enableResizeDetection: true,
    scrollTriggerCallback: () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const targets = rootEl.querySelectorAll<HTMLElement>('[data-sp2-anim]');
      targets.forEach((el) => {
        const dir = el.dataset.sp2Anim === 'right' ? 'animate__fadeInRight' : 'animate__fadeInLeft';
        const delay = el.dataset.sp2Delay || '0s';
        el.classList.add('animate__animated');
        el.classList.remove('animate__fadeInLeft', 'animate__fadeInRight');
        void el.offsetWidth; // 强制回流，确保可重复触发
        el.classList.add(dir);
        el.style.animationDelay = delay;
        el.style.animationDuration = '0.8s';
      });
    }
  });
  return (
    <section className={styles['screen2-cover']} ref={rootRef}>
      <section>
        <div className={styles['screen2-wrap']}>
          <div className={styles['screen2-h1']}>
            <span>爱学AI自然拼读</span>
          </div>
        </div>
      </section>

      {/* Section 1 */}
      <section className={styles['screen2-section']}>
        <div className={styles['screen2-wrap']}>
          <div className={styles['screen2-twoCol']} data-sp2-anim="left" data-sp2-delay="0.1s">
            <div>
              <img
                className={`${styles['screen2-cardImg']} ${styles['screen2-cardImg-left']}`}
                src={detail1Src}
                alt="精准音素级教学"
              />
            </div>
            <div className={styles['screen2-textCol']}>
              <div className={`${styles['screen2-h2Mark']} ${styles['screen2-h2Mark--a']}`}></div>
              <div className={styles['screen2-h2Wrap']}>
                <div className={styles['screen2-h2']}>精准音素级教学，发音标准很简单</div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>音素拆解与合成</div>
                <div className={styles['screen2-pointDesc']}>
                  AI实时分解单词至最小发音单位（如"cat"→/k/-/æ/-/t/），数字人外教的口型、舌位可视化演示精度达98%，吻合真实发音器官运动（基于EMA电磁发音数据）。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>动态对比训练</div>
                <div className={styles['screen2-pointDesc']}>
                  自动检测易混淆音素（如/s/ vs
                  /ʃ/），生成最小对立对训练（仅有一个音素不同的单词对，通过对比和反复练习），来强化学习对特定语音差异的感知、区分和表达能力。
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 */}
      <section className={styles['screen2-section']}>
        <div className={styles['screen2-wrap']}>
          <div className={styles['screen2-twoCol']} data-sp2-anim="right" data-sp2-delay="0.4s">
            <div className={styles['screen2-textCol']}>
              <div className={`${styles['screen2-h2Mark']} ${styles['screen2-h2Mark--b']}`}></div>
              <div className={styles['screen2-h2Wrap']}>
                <div className={styles['screen2-h2']}>个性化教学系统</div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>智能学前诊断</div>
                <div className={styles['screen2-pointDesc']}>
                  课前定级测试，3.5分钟快速检测字母发音对应难点、拼读规则（如CVC/CVCE）掌握程度、字母的熟悉程度及字母和字母组合的发音掌握程度，完成英语语言的能力初评。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>实时难度调控</div>
                <div className={styles['screen2-pointDesc']}>
                  <span style={{ display: 'inline-block', width: '100%' }}>
                    对"慢热型"学习者：自动增加多感官强化
                  </span>
                  <span style={{ display: 'inline-block', width: '100%' }}>
                    对"挑战型"学习者：为每个lesson设置多维度目标和达成路径
                  </span>
                  <span>课堂用语和教学指令根据学员的程度自动调整</span>
                </div>
              </div>
            </div>
            <div>
              <img
                className={`${styles['screen2-cardImg']} ${styles['screen2-cardImg-right']}`}
                src={detail2Src}
                alt="个性化教学系统"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section className={styles['screen2-section']}>
        <div className={styles['screen2-wrap']}>
          <div className={styles['screen2-twoCol']} data-sp2-anim="left" data-sp2-delay="0.7s">
            <img
              className={`${styles['screen2-cardImg']} ${styles['screen2-cardImg-left']}`}
              src={detail3Src}
              alt="游戏化设计培养自主学习能力"
            />
            <div className={styles['screen2-textCol']}>
              <div className={`${styles['screen2-h2Mark']} ${styles['screen2-h2Mark--a']}`}></div>
              <div className={styles['screen2-h2Wrap']}>
                <div className={styles['screen2-h2']}>游戏化设计培养自主学习能力</div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>游戏里藏规律</div>
                <div className={styles['screen2-pointDesc']}>
                  不直接讲规则，而是让你在有趣的任务里，发现字母组合的秘密！
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>让错误变明显</div>
                <div className={styles['screen2-pointDesc']}>
                  读错时会用不同颜色圈出音节，马上知道错在哪里怎么改。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>送你学习秘籍</div>
                <div className={styles['screen2-pointDesc']}>
                  每节课后，给你专属小建议，让你越学越会学!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section className={styles['screen2-section']}>
        <div className={styles['screen2-wrap']}>
          <div className={styles['screen2-twoCol']} data-sp2-anim="right" data-sp2-delay="1s">
            <div className={styles['screen2-textCol']}>
              <div className={`${styles['screen2-h2Mark']} ${styles['screen2-h2Mark--b']}`}></div>
              <div className={styles['screen2-h2Wrap']}>
                <div className={styles['screen2-h2']}>全闭环学习系统</div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>趣味化入门</div>
                <div className={styles['screen2-pointDesc']}>
                  通过动画故事和互动儿歌，在游戏中轻松入门，激发学习兴趣。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>智能化练习</div>
                <div className={styles['screen2-pointDesc']}>
                  通过智能语音实时反馈，帮你精准打磨发音，形成肌肉记忆。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>个性化输出</div>
                <div className={styles['screen2-pointDesc']}>
                  为你量身生成趣味任务，真正把学到的知识用起来，巩固记忆。
                </div>
              </div>
              <div className={styles['screen2-point']}>
                <div className={styles['screen2-pointTitle']}>成长性反馈</div>
                <div className={styles['screen2-pointDesc']}>
                  不是简单判断对错，而是精确到每个音素的纠错和成长性评分，让你看到自己的每一点进步。
                </div>
              </div>
            </div>
            <img
              className={`${styles['screen2-cardImg']} ${styles['screen2-cardImg-right']}`}
              src={detail4Src}
              alt="全闭环学习系统"
            />
          </div>
        </div>
      </section>
    </section>
  );
}
